import {
  Bid,
  BidGroup,
  TimberItem,
  DesignBucket,
  DesignLineItem,
  ProductionBucket,
  FreightBucket,
  InstallBucket,
  InstallLineItem,
  MaterialsBucket,
  ManualMaterialItem,
  PRODUCTION_RATE_PER_BOARD_FOOT,
  DEFAULT_DESIGN_DAY_RATES,
} from '../types/bidding';
import { calculateBoardFeet } from './timberListParser';

// ==========================================
// DESIGN BUCKET CALCULATIONS
// ==========================================

/**
 * Calculate total for design bucket
 */
export function calculateDesignBucketTotal(bucket: DesignBucket): number {
  return bucket.lineItems.reduce((sum, item) => sum + item.billableTotal, 0);
}

/**
 * Calculate design line item costs
 */
export function calculateDesignLineItem(item: DesignLineItem): DesignLineItem {
  const cost = item.dayRate * item.days;
  
  return {
    ...item,
    cost,
    billableTotal: item.billableTotal || cost, // Use existing billableTotal or default to cost
  };
}

/**
 * Create a default design bucket with lump sum estimates
 */
export function createDefaultDesignBucket(): DesignBucket {
  return {
    lineItems: [
      {
        id: generateId(),
        category: 'design',
        description: 'Timber Frame Design',
        dayRate: DEFAULT_DESIGN_DAY_RATES.design,
        days: 0,
        cost: 0,
        billableTotal: 0,
      },
      {
        id: generateId(),
        category: 'engineering',
        description: 'Structural Engineering',
        dayRate: DEFAULT_DESIGN_DAY_RATES.engineering,
        days: 0,
        cost: 0,
        billableTotal: 0,
      },
      {
        id: generateId(),
        category: 'overhead',
        description: 'Design Overhead',
        dayRate: DEFAULT_DESIGN_DAY_RATES.overhead,
        days: 0,
        cost: 0,
        billableTotal: 0,
      },
    ],
    total: 0,
  };
}

// ==========================================
// PRODUCTION BUCKET CALCULATIONS
// ==========================================

/**
 * Calculate total board feet from timber list
 */
export function calculateTotalBoardFeet(timberList: TimberItem[]): number {
  return timberList.reduce((sum, item) => {
    const bf = calculateBoardFeet(item);
    return sum + (bf * item.quantity);
  }, 0);
}

/**
 * Calculate production bucket total
 */
export function calculateProductionBucketTotal(bucket: ProductionBucket): number {
  return bucket.totalBoardFeet * bucket.ratePerBoardFoot;
}

/**
 * Create production bucket from timber list
 */
export function createProductionBucket(timberList: TimberItem[]): ProductionBucket {
  const totalBoardFeet = calculateTotalBoardFeet(timberList);
  
  return {
    totalBoardFeet,
    ratePerBoardFoot: PRODUCTION_RATE_PER_BOARD_FOOT,
    total: totalBoardFeet * PRODUCTION_RATE_PER_BOARD_FOOT,
  };
}

// ==========================================
// FREIGHT BUCKET CALCULATIONS
// ==========================================

/**
 * Calculate freight bucket total
 */
export function calculateFreightBucketTotal(bucket: FreightBucket): number {
  return bucket.inbound + bucket.outbound;
}

/**
 * Create a default freight bucket
 */
export function createDefaultFreightBucket(): FreightBucket {
  return {
    inbound: 0,
    outbound: 0,
    total: 0,
  };
}

// ==========================================
// INSTALL BUCKET CALCULATIONS
// ==========================================

/**
 * Calculate total for install bucket
 */
export function calculateInstallBucketTotal(bucket: InstallBucket): number {
  return bucket.lineItems.reduce((sum, item) => sum + item.billableTotal, 0);
}

/**
 * Calculate install line item costs
 */
export function calculateInstallLineItem(item: InstallLineItem): InstallLineItem {
  const cost = item.rate * item.quantity;
  
  return {
    ...item,
    cost,
    billableTotal: item.billableTotal || cost,
  };
}

/**
 * Create a default install bucket with preloaded items
 */
export function createDefaultInstallBucket(installDays: number = 1): InstallBucket {
  const lineItems: InstallLineItem[] = [
    // Labor
    {
      id: generateId(),
      type: 'labor',
      description: 'Crew Lead',
      rate: 500,
      rateUnit: 'day',
      quantity: installDays,
      cost: 500 * installDays,
      billableTotal: 500 * installDays,
    },
    {
      id: generateId(),
      type: 'labor',
      description: 'Carpenter',
      rate: 400,
      rateUnit: 'day',
      quantity: installDays,
      cost: 400 * installDays,
      billableTotal: 400 * installDays,
    },
    {
      id: generateId(),
      type: 'labor',
      description: 'Helper',
      rate: 300,
      rateUnit: 'day',
      quantity: installDays,
      cost: 300 * installDays,
      billableTotal: 300 * installDays,
    },
    // Equipment
    {
      id: generateId(),
      type: 'equipment',
      description: 'Crane Hire',
      rate: 1600,
      rateUnit: 'day',
      quantity: installDays,
      cost: 1600 * installDays,
      billableTotal: 1600 * installDays,
    },
    {
      id: generateId(),
      type: 'equipment',
      description: 'Telehandler Rental',
      rate: 500,
      rateUnit: 'day',
      quantity: installDays,
      cost: 500 * installDays,
      billableTotal: 500 * installDays,
    },
    {
      id: generateId(),
      type: 'equipment',
      description: 'Scissor Lift Rental',
      rate: 350,
      rateUnit: 'day',
      quantity: installDays,
      cost: 350 * installDays,
      billableTotal: 350 * installDays,
    },
  ];
  
  return {
    totalInstallDays: installDays,
    lineItems,
    total: lineItems.reduce((sum, item) => sum + item.billableTotal, 0),
  };
}

/**
 * Add default install line items (labor, equipment, overhead)
 * @deprecated Use createDefaultInstallBucket instead
 */
export function addDefaultInstallLineItems(
  bucket: InstallBucket,
  installDays: number = 1
): InstallBucket {
  return createDefaultInstallBucket(installDays);
}

// ==========================================
// MATERIALS BUCKET CALCULATIONS
// ==========================================

/**
 * Calculate total cost of timbers
 */
export function calculateTimbersCost(timberList: TimberItem[]): number {
  return timberList.reduce((sum, item) => sum + item.totalPrice, 0);
}

/**
 * Calculate total cost of manual materials
 */
export function calculateManualMaterialsCost(items: ManualMaterialItem[]): number {
  return items.reduce((sum, item) => sum + item.totalPrice, 0);
}

/**
 * Calculate materials bucket total
 */
export function calculateMaterialsBucketTotal(bucket: MaterialsBucket): number {
  return bucket.timbers.totalCost + calculateManualMaterialsCost(bucket.manualItems);
}

/**
 * Calculate manual material item total
 */
export function calculateManualMaterialItem(item: ManualMaterialItem): ManualMaterialItem {
  return {
    ...item,
    totalPrice: item.quantity * item.unitPrice,
  };
}

/**
 * Create materials bucket from timber list with preloaded common items
 */
export function createMaterialsBucket(timberList: TimberItem[]): MaterialsBucket {
  const totalCost = calculateTimbersCost(timberList);
  
  // Preload common materials with 0 quantity (user fills in)
  const manualItems: ManualMaterialItem[] = [
    {
      id: generateId(),
      description: 'Timberlinx A475 Post Bases',
      quantity: 0,
      unit: 'ea',
      unitPrice: 80,
      totalPrice: 0,
    },
    {
      id: generateId(),
      description: 'Pegs',
      quantity: 0,
      unit: 'ea',
      unitPrice: 1.5,
      totalPrice: 0,
    },
    {
      id: generateId(),
      description: 'Fasteners (SIP screws, lags, etc.)',
      quantity: 1,
      unit: 'lot',
      unitPrice: 0,
      totalPrice: 0,
    },
  ];
  
  return {
    timbers: {
      items: timberList,
      totalCost,
    },
    manualItems,
    total: totalCost,
  };
}

// ==========================================
// GROUP CALCULATIONS
// ==========================================

/**
 * Calculate group subtotal (sum of all 5 buckets)
 */
export function calculateGroupSubtotal(group: BidGroup): number {
  return (
    group.designBucket.total +
    group.productionBucket.total +
    group.freightBucket.total +
    group.installBucket.total +
    group.materialsBucket.total
  );
}

/**
 * Calculate group total (subtotal with profit margin)
 */
export function calculateGroupTotal(group: BidGroup): number {
  const subtotal = calculateGroupSubtotal(group);
  return subtotal * (1 + group.profitMargin);
}

/**
 * Recalculate all totals for a group
 */
export function recalculateGroup(group: BidGroup): BidGroup {
  // Recalculate each bucket
  const designBucket = {
    ...group.designBucket,
    total: calculateDesignBucketTotal(group.designBucket),
  };
  
  const productionBucket = {
    ...group.productionBucket,
    total: calculateProductionBucketTotal(group.productionBucket),
  };
  
  const freightBucket = {
    ...group.freightBucket,
    total: calculateFreightBucketTotal(group.freightBucket),
  };
  
  const installBucket = {
    ...group.installBucket,
    total: calculateInstallBucketTotal(group.installBucket),
  };
  
  const materialsBucket = {
    ...group.materialsBucket,
    total: calculateMaterialsBucketTotal(group.materialsBucket),
  };
  
  // Calculate group totals
  const subtotal = 
    designBucket.total +
    productionBucket.total +
    freightBucket.total +
    installBucket.total +
    materialsBucket.total;
  
  const total = subtotal * (1 + group.profitMargin);
  
  return {
    ...group,
    designBucket,
    productionBucket,
    freightBucket,
    installBucket,
    materialsBucket,
    subtotal,
    total,
  };
}

/**
 * Create a default group with preloaded buckets
 */
export function createDefaultGroup(name: string, order: number): BidGroup {
  const installBucket = createDefaultInstallBucket(1);
  const materialsBucket = createMaterialsBucket([]);
  
  return {
    id: generateId(),
    name,
    order,
    timberList: [],
    designBucket: createDefaultDesignBucket(),
    productionBucket: {
      totalBoardFeet: 0,
      ratePerBoardFoot: PRODUCTION_RATE_PER_BOARD_FOOT,
      total: 0,
    },
    freightBucket: createDefaultFreightBucket(),
    installBucket,
    materialsBucket,
    subtotal: installBucket.total,
    profitMargin: 0.5, // Default 50%
    total: installBucket.total * 1.5,
  };
}

// ==========================================
// BID CALCULATIONS
// ==========================================

/**
 * Calculate bid grand total (sum of all group totals)
 */
export function calculateBidGrandTotal(bid: Bid): number {
  return bid.groups.reduce((sum, group) => sum + group.total, 0);
}

/**
 * Recalculate all groups and totals for a bid
 */
export function recalculateBid(bid: Bid): Bid {
  const updatedGroups = bid.groups.map(group => recalculateGroup(group));
  
  return {
    ...bid,
    groups: updatedGroups,
  };
}

/**
 * Get bid summary statistics
 */
export function getBidSummary(bid: Bid) {
  const grandTotal = calculateBidGrandTotal(bid);
  const totalBoardFeet = bid.groups.reduce(
    (sum, group) => sum + group.productionBucket.totalBoardFeet,
    0
  );
  const totalTimberItems = bid.groups.reduce(
    (sum, group) => sum + group.timberList.length,
    0
  );
  
  return {
    grandTotal,
    totalBoardFeet,
    totalTimberItems,
    groupCount: bid.groups.length,
    avgProfitMargin: bid.groups.reduce((sum, g) => sum + g.profitMargin, 0) / bid.groups.length,
  };
}

// ==========================================
// HELPER FUNCTIONS
// ==========================================

/**
 * Generate a unique ID
 */
function generateId(): string {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * Format currency
 */
export function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
}

/**
 * Format percentage
 */
export function formatPercentage(decimal: number): string {
  return `${(decimal * 100).toFixed(1)}%`;
}

/**
 * Format board feet
 */
export function formatBoardFeet(bf: number): string {
  return `${bf.toFixed(2)} BF`;
}
